import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAPI } from '../../../Contexts/APIContext'
import { useAuth } from '../../../Contexts/AuthContext'
import classes from './index.module.css'
import { useArtistSelection } from '../../../Helpers/hooks/useArtistSelection'
import ExplicitTag from '../ExplicitTag'
import EditableNameInput from '../EditableNameInput/EditableNameInput'
import errorToast from '../../../Components/Toasts/ErrorToast'
import successToast from '../../../Components/Toasts/SuccessToast'
export default function ArtistAndSongName({ song, badge, isReadOnly = false, isInternal = false }) {
  const artist = song.artist
  const { handleArtistSelection } = useArtistSelection()
  const { updateSongName } = useAPI()
  const { getCurrentUser } = useAuth()
  const queryClient = useQueryClient()
  const [localSongName, setLocalSongName] = useState(song?.name || '')

  const updateSongNameMutation = useMutation({
    mutationFn: (newName) =>
      updateSongName(song.f_id, newName, getCurrentUser()?.email),
    onSuccess: () => {
      // Clear relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['songData', song.f_id] })
      queryClient.invalidateQueries({ queryKey: ['songFeels', song.f_id] })
      // Show success notification
      successToast('Song name updated successfully')
    },
    onError: (error) => {
      console.error('Failed to update song name:', error)
      // Revert to original name on error
      setLocalSongName(song?.name || '')
      // Show error notification
      errorToast('Failed to update song name')
    },
  })

  const handleSongNameChange = (newName) => {
    if (newName && newName !== localSongName) {
      setLocalSongName(newName) // Update local state immediately
      updateSongNameMutation.mutate(newName)
    }
  }

  // Update local state when song prop changes
  React.useEffect(() => {
    setLocalSongName(song?.name || '')
  }, [song?.name])

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <h1 className={classes.songTitle} style={{ display: 'flex', alignItems: 'center' }}>
          {(isReadOnly || !isInternal) ? (
            <span>{localSongName}</span>
          ) : (
            <EditableNameInput
              name={localSongName}
              onNameChange={handleSongNameChange}
              placeholder="Song name"
              doubleClickToEdit={false}
            />
          )}
        </h1>
        {song?.is_explicit && <ExplicitTag />}
        {badge ? <div className={classes.badge}>{badge}</div> : null}
      </div>
      <button
        className={classes.artistWrapper}
        onClick={() => {
          handleArtistSelection(artist)
        }}
      >
        {song?.artist?.image?.url ? (
          <img className={classes.avatar} src={song?.artist?.image?.url} />
        ) : (
          <div className={classes.emptyAvatarWrapper}>
            <p className={classes.emptyAvatarLetter}>
              {song?.artist?.name.charAt(0).toUpperCase()}
            </p>
          </div>
        )}
        <p className={classes.artistName}>{song?.song_artist_name || song?.artist?.name}</p>
      </button>
    </div>
  )
}

ArtistAndSongName.propTypes = {
  song: PropTypes.object.isRequired,
  badge: PropTypes.node,
  isReadOnly: PropTypes.bool,
  isInternal: PropTypes.bool,
}
